import React from 'react';
import { Zap, HelpCircle } from 'lucide-react';
import { 
  BatchItemSimulationResult, 
  BatchItemInput, 
  TaxRegimeMode, 
  TaxReductionCategory 
} from '../../lib/batchTaxSimulatorEngine';

export interface BatchSimulatorTableProps {
  results: BatchItemSimulationResult[];
  batchItems: BatchItemInput[];
  activePanelTab: 'entries' | 'exits' | 'consolidation';
  taxRegime: TaxRegimeMode;
  targetMarginPct: number;
  onUpdateItem: (index: number, patch: Partial<BatchItemInput>) => void;
  onOpenSimulationModal: (index: number) => void;
  formatBRL: (val: number) => string;
  formatPct: (val: number) => string;
}

export default function BatchSimulatorTable({
  results,
  batchItems,
  activePanelTab,
  taxRegime,
  targetMarginPct,
  onUpdateItem,
  onOpenSimulationModal,
  formatBRL,
  formatPct
}: BatchSimulatorTableProps) {
  return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
            <span>Tabela Detalhada por Item & Simulação de Notas</span>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
              Editável em Tempo Real
            </span>
          </h4>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Clique no botão ⚡ Simular NF ou ajuste o custo de entrada e o benefício fiscal diretamente na linha.
          </p>
        </div>
        <span className="text-[11px] text-slate-500">
          Regime: <strong>{taxRegime.replace('_', ' ').toUpperCase()}</strong> | Margem Alvo: <strong>{targetMarginPct}%</strong>
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs text-slate-700">
          <thead className="border-b border-slate-200 text-[10px] font-black uppercase tracking-wider">
            {activePanelTab === 'entries' ? (
              /* Painel 📥 Notas de Entrada & Insumos */
              <tr className="bg-emerald-100/70 text-emerald-950 border-b border-emerald-200">
                <th className="py-2.5 px-3">Item / Insumo / CFOP</th>
                <th className="py-2.5 px-2 text-center font-mono">NCM</th>
                <th className="py-2.5 px-2 text-center font-mono">NBS</th>
                <th className="py-2.5 px-2 text-right">NF Entrada (Custo Total R$)</th>
                <th className="py-2.5 px-2 text-right">Custo Unitário (R$)</th>
                <th className="py-2.5 px-2 text-right text-slate-700">Crédito Atual Legado</th>
                <th className="py-2.5 px-2 text-center bg-emerald-50/80 text-emerald-950">Alíq. Reforma</th>
                <th className="py-2.5 px-2 text-right text-emerald-800">Crédito CBS+IBS (28%)</th>
                <th className="py-2.5 px-3 text-right bg-emerald-200/60 text-emerald-950 font-black">Ganho Líquido (+R$)</th>
                <th className="py-2.5 px-2 text-center">Simular</th>
              </tr>
            ) : activePanelTab === 'exits' ? (
              /* Painel 📤 Notas de Saída & Catálogo */
              <tr className="bg-indigo-100/70 text-indigo-950 border-b border-indigo-200">
                <th className="py-2.5 px-3">Item / Produto / Serviço / CFOP</th>
                <th className="py-2.5 px-2 text-center font-mono">NCM</th>
                <th className="py-2.5 px-2 text-center font-mono">NBS</th>
                <th className="py-2.5 px-2 text-right">Preço de Venda Atual</th>
                <th className="py-2.5 px-2 text-center bg-indigo-50/80 text-indigo-950">Alíq. Saída Reforma</th>
                <th className="py-2.5 px-3 text-right bg-emerald-50/80 text-emerald-950 font-black">Novo Preço Sugerido (Degrau R$ 5)</th>
                <th className="py-2.5 px-2 text-right text-rose-700">Débito Saída (CBS+IBS)</th>
                <th className="py-2.5 px-2 text-right">Margem Projetada</th>
                <th className="py-2.5 px-3 text-center">Diagnóstico</th>
                <th className="py-2.5 px-2 text-center">Simular</th>
              </tr>
            ) : (
              /* Painel ⚖️ Apuração Consolidada & Capital de Giro */
              <>
                {/* Linha 1: Seções Fiscais Agrupadas */}
                <tr className="bg-slate-100/80 border-b border-slate-200 text-slate-600">
                  <th colSpan={3} className="py-2 px-3 border-r border-slate-200">
                    Identificação do Item & Códigos Fiscais
                  </th>
                  <th colSpan={3} className="py-2 px-3 text-center border-r border-slate-200 bg-emerald-100/50 text-emerald-950">
                    📥 Notas de Entrada (Compras)
                  </th>
                  <th colSpan={4} className="py-2 px-3 text-center border-r border-slate-200 bg-indigo-100/50 text-indigo-950">
                    📤 Notas de Saída (Vendas & Nova Reforma)
                  </th>
                  <th colSpan={3} className="py-2 px-3 text-center border-r border-slate-200 bg-slate-200/50 text-slate-800">
                    ⚖️ Apuração & Margem
                  </th>
                  <th className="py-2 px-2 text-center bg-slate-200/50 text-slate-800">
                    Ação
                  </th>
                </tr>
                {/* Linha 2: Colunas Detalhadas com NCM e NBS Separados */}
                <tr className="bg-slate-50 text-slate-500">
                  <th className="py-2.5 px-3">Item / SKU / CFOP</th>
                  <th className="py-2.5 px-2 text-center font-mono text-[10px]">NCM</th>
                  <th className="py-2.5 px-2 text-center font-mono text-[10px] border-r border-slate-200">NBS</th>
                  
                  {/* Entrada */}
                  <th className="py-2.5 px-2 text-right">NF Entrada (Custo)</th>
                  <th className="py-2.5 px-2 text-center bg-emerald-50/70 text-emerald-950">Alíq. Entrada</th>
                  <th className="py-2.5 px-2 text-right border-r border-slate-200 text-emerald-800">Crédito Entrada</th>
                  
                  {/* Saída */}
                  <th className="py-2.5 px-2 text-right">Preço Atual</th>
                  <th className="py-2.5 px-2 text-center bg-indigo-50/70 text-indigo-950">Alíq. Saída</th>
                  <th className="py-2.5 px-3 text-right bg-emerald-50/60 text-emerald-950 font-black">Novo Preço Sugerido</th>
                  <th className="py-2.5 px-2 text-right border-r border-slate-200 text-rose-700">Débito Saída</th>
                  
                  {/* Apuração */}
                  <th className="py-2.5 px-2 text-right">A Recolher (Split)</th>
                  <th className="py-2.5 px-2 text-right">Margem Nova</th>
                  <th className="py-2.5 px-3 text-center border-r border-slate-200">Diagnóstico</th>
                  <th className="py-2.5 px-2 text-center">Simular</th>
                </tr>
              </>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {results.map((r) => {
              const idx = r.originalIndex;
              const isSalesOnlyItem = r.isSalesOnly;
              const isConsumptionItem = r.destination === 'consumption';
              return (
                <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                  {activePanelTab === 'entries' ? (
                    <>
                      {/* 1. Item / Insumo / CFOP */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-slate-900 leading-snug">{r.name}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="text-[10px] text-slate-400 font-mono">{r.sku}</span>
                          <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold ${
                            isConsumptionItem
                              ? 'bg-slate-100 text-slate-700 border border-slate-300'
                              : r.destination === 'raw_material'
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : r.sector === 'servico' || r.destination === 'service'
                              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {isConsumptionItem 
                              ? 'Uso & Consumo' 
                              : r.destination === 'raw_material'
                              ? 'Matéria-Prima'
                              : r.destination === 'service'
                              ? 'Serviço'
                              : 'Revenda'}
                          </span>
                          {r.cfop && (
                            <span className="text-[9px] font-mono text-slate-700 bg-slate-100 px-1 py-0.2 rounded border border-slate-200 font-semibold" title={r.cfopDescription || `CFOP ${r.cfop}`}>
                              CFOP {r.cfop}
                            </span>
                          )}
                          {r.cfopDescription && (
                            <span className="text-[9px] text-slate-500 truncate max-w-[150px]" title={r.cfopDescription}>
                              {r.cfopDescription}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 2. NCM */}
                      <td className="py-2.5 px-2 text-center">
                        {r.ncm && r.ncm !== '-' && r.ncm !== '0000.00.00' && r.fiscalType === 'NCM' ? (
                          <span className="font-mono text-[11px] font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 inline-block">
                            {r.ncm}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-mono text-xs">-</span>
                        )}
                      </td>

                      {/* 3. NBS */}
                      <td className="py-2.5 px-2 text-center">
                        {r.fiscalType === 'NBS' ? (
                          <span className="font-mono text-[11px] font-bold text-indigo-800 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200 inline-block">
                            {r.nbs || r.fiscalCode}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-mono text-xs">-</span>
                        )}
                      </td>

                      {/* 4. NF Entrada (Custo Total R$) */}
                      <td className="py-2.5 px-2 text-right">
                        <div className="inline-flex items-center justify-end gap-1">
                          <span className="text-[10px] text-slate-400">R$</span>
                          <input
                            type="number"
                            step="0.10"
                            min="0"
                            value={batchItems[idx]?.costPrice ?? r.originalCost}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              onUpdateItem(idx, { costPrice: isNaN(val) ? 0 : val });
                            }}
                            className="w-16 px-1.5 py-0.5 text-right font-bold text-slate-900 bg-slate-50 hover:bg-white border border-slate-200 focus:border-emerald-500 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            title="Clique para editar o custo total da NF"
                          />
                        </div>
                      </td>

                      {/* 5. Custo Unitário (R$) */}
                      <td className="py-2.5 px-2 text-right">
                        <span className="font-mono text-xs font-semibold text-slate-700">
                          {formatBRL(r.unitCost || r.originalCost)}
                        </span>
                      </td>

                      {/* 6. Crédito Atual Legado */}
                      <td className="py-2.5 px-2 text-right">
                        {r.isCreditDisallowedToday ? (
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-slate-400">R$ 0,00</span>
                            <span className="text-[9px] font-black text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200" title="Crédito vedado pela Lei Kandir (LC 87/96)">
                              Vedado LC 87/96
                            </span>
                          </div>
                        ) : (
                          <div>
                            <div className="font-bold text-slate-700 text-xs">
                              {formatBRL(r.legacyCreditVal)}
                            </div>
                            <span className="text-[9px] text-slate-400 block font-mono">
                              ICMS+PIS/COF
                            </span>
                          </div>
                        )}
                      </td>

                      {/* 7. Alíquota Reforma */}
                      <td className="py-2.5 px-2 text-center bg-emerald-50/20">
                        <div className="flex flex-col items-center">
                          <span className="px-2 py-0.5 rounded-md font-black text-[11px] bg-emerald-100 text-emerald-900 border border-emerald-200">
                            {r.inputCreditPct > 0 ? `${r.inputCreditPct.toFixed(1)}%` : '0,0%'}
                          </span>
                          <span className="text-[9px] text-slate-500 font-mono mt-0.5 whitespace-nowrap">
                            CBS {r.inputCreditCbsRatePct.toFixed(1)}% | IBS {r.inputCreditIbsRatePct.toFixed(1)}%
                          </span>
                          <select
                            value={batchItems[idx]?.supplierTaxRegime || 'regime_geral'}
                            onChange={(e) => onUpdateItem(idx, { supplierTaxRegime: e.target.value as any })}
                            className="mt-1 text-[9px] font-bold bg-white border border-slate-200 rounded px-1 py-0.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                            title="Regime fiscal do fornecedor"
                          >
                            <option value="regime_geral">Geral (28%)</option>
                            <option value="simples_nacional">Simples (4%)</option>
                            <option value="isento_zero">Cesta Zero (0%)</option>
                          </select>
                        </div>
                      </td>

                      {/* 8. Crédito CBS+IBS (Reforma) */}
                      <td className="py-2.5 px-2 text-right">
                        <div className="font-bold text-emerald-700 text-xs">
                          +{formatBRL(r.inputCreditVal)}
                        </div>
                        <span className="text-[9px] text-slate-500 block font-mono whitespace-nowrap">
                          CBS: +{formatBRL(r.inputCreditCbsVal)} | IBS: +{formatBRL(r.inputCreditIbsVal)}
                        </span>
                      </td>

                      {/* 9. Ganho Líquido na Reforma (+R$) */}
                      <td className="py-2.5 px-3 text-right bg-emerald-50/60">
                        <div className="font-black text-emerald-800 text-sm">
                          +{formatBRL(r.reformCreditGainVal)}
                        </div>
                        <span className="text-[9px] text-emerald-700 font-semibold block">
                          {r.isCreditDisallowedToday ? 'Crédito Novo Desbloqueado' : 'Ganho vs Legado'}
                        </span>
                      </td>

                      {/* 10. Botão Simular */}
                      <td className="py-2.5 px-2 text-center">
                        <button
                          type="button"
                          onClick={() => onOpenSimulationModal(idx)}
                          className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 transition-colors inline-flex items-center justify-center gap-1 text-[11px] font-bold cursor-pointer"
                          title="Abrir Simulador de Nota Fiscal"
                        >
                          <Zap className="w-3.5 h-3.5 text-emerald-700" />
                        </button>
                      </td>
                    </>
                  ) : activePanelTab === 'exits' ? (
                    <>
                      {/* 1. Item / Produto / Serviço / CFOP */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-slate-900 leading-snug">{r.name}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="text-[10px] text-slate-400 font-mono">{r.sku}</span>
                          {r.cfop && (
                            <span className="text-[9px] font-mono text-slate-600 bg-slate-100 px-1 py-0.2 rounded border border-slate-200">
                              CFOP {r.cfop}
                            </span>
                          )}
                          {r.paymentTermDays !== undefined && r.paymentTermDays > 0 && (
                            <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.2 rounded" title="Prazo de recebimento da venda">
                              PMR: {r.paymentTermDays}d
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 2. NCM */}
                      <td className="py-2.5 px-2 text-center">
                        {r.ncm && r.ncm !== '-' && r.ncm !== '0000.00.00' && r.fiscalType === 'NCM' ? (
                          <span className="font-mono text-[11px] font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 inline-block">
                            {r.ncm}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-mono text-xs">-</span>
                        )}
                      </td>

                      {/* 3. NBS */}
                      <td className="py-2.5 px-2 text-center">
                        {r.fiscalType === 'NBS' ? (
                          <span className="font-mono text-[11px] font-bold text-indigo-800 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200 inline-block">
                            {r.nbs || r.fiscalCode}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-mono text-xs">-</span>
                        )}
                      </td>

                      {/* 4. Preço de Venda Atual */}
                      <td className="py-2.5 px-2 text-right text-slate-700 font-bold">
                        {formatBRL(r.currentSalePrice)}
                      </td>

                      {/* 5. Alíquota Saída Reforma */}
                      <td className="py-2.5 px-2 text-center bg-indigo-50/20">
                        <div className="flex flex-col items-center">
                          <span className={`px-2 py-0.5 rounded-md font-black text-[11px] border ${
                            r.reductionCategory === 'zero'
                              ? 'bg-emerald-100 text-emerald-900 border-emerald-200'
                              : r.reductionCategory === 'reducao_60'
                              ? 'bg-blue-100 text-blue-900 border-blue-200'
                              : r.reductionCategory === 'reducao_30'
                              ? 'bg-indigo-100 text-indigo-900 border-indigo-200'
                              : r.reductionCategory === 'seletivo'
                              ? 'bg-amber-100 text-amber-900 border-amber-200'
                              : 'bg-slate-100 text-slate-800 border-slate-200'
                          }`}>
                            {r.totalTaxRatePct.toFixed(1)}%
                          </span>
                          <span className="text-[9px] text-slate-500 font-mono mt-0.5 whitespace-nowrap">
                            CBS {r.cbsRatePct.toFixed(1)}% | IBS {r.ibsRatePct.toFixed(1)}%
                          </span>
                          <select
                            value={r.reductionCategory}
                            onChange={(e) => onUpdateItem(idx, { taxReduction: e.target.value as any })}
                            className="mt-1 text-[9px] font-bold bg-white border border-slate-200 rounded px-1 py-0.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
                          >
                            <option value="zero">Zero (0%)</option>
                            <option value="reducao_60">Red. 60% (11.2%)</option>
                            <option value="reducao_30">Red. 30% (19.6%)</option>
                            <option value="padrao">Padrão (28%)</option>
                            <option value="seletivo">Seletivo (35%)</option>
                          </select>
                        </div>
                      </td>

                      {/* 6. Novo Preço Sugerido (Degrau de R$ 5) */}
                      <td className="py-2.5 px-3 text-right bg-emerald-50/50">
                        <div className="font-black text-emerald-900 text-sm">
                          {formatBRL(r.suggestedSalePrice)}
                        </div>
                        {isSalesOnlyItem ? (
                          <span className="text-[9px] text-emerald-700 font-medium block">
                            (Preço Atual Preservado)
                          </span>
                        ) : r.simulatedSalePrice !== r.suggestedSalePrice ? (
                          <span className="text-[10px] text-indigo-700 font-bold block">
                            Simulado: {formatBRL(r.simulatedSalePrice)}
                          </span>
                        ) : null}
                      </td>

                      {/* 7. Débito Saída (CBS+IBS) */}
                      <td className="py-2.5 px-2 text-right">
                        <div className="font-bold text-rose-700 text-xs">
                          {formatBRL(r.taxOnSaleVal)}
                        </div>
                        {r.taxOnSaleVal > 0 && (
                          <span className="text-[9px] text-slate-500 block font-mono whitespace-nowrap">
                            CBS: {formatBRL(r.taxOnSaleCbsVal)} | IBS: {formatBRL(r.taxOnSaleIbsVal)}
                          </span>
                        )}
                      </td>

                      {/* 8. Margem Projetada */}
                      <td className="py-2.5 px-2 text-right font-bold text-slate-800">
                        {formatPct(r.projectedMarginPct)}
                        <span className="block text-[9px] text-slate-400 font-normal">
                          {formatBRL(r.projectedMarginVal)}
                        </span>
                      </td>

                      {/* 9. Diagnóstico */}
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${
                          r.status === 'HEALTHY'
                            ? 'bg-emerald-100 text-emerald-800'
                            : r.status === 'COMPRESSED'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}>
                          {r.statusLabel}
                        </span>
                      </td>

                      {/* 10. Botão Simular */}
                      <td className="py-2.5 px-2 text-center">
                        <button
                          type="button"
                          onClick={() => onOpenSimulationModal(idx)}
                          className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 transition-colors inline-flex items-center justify-center gap-1 text-[11px] font-bold cursor-pointer"
                          title="Abrir Simulador de Nota Fiscal"
                        >
                          <Zap className="w-3.5 h-3.5 text-emerald-700" />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      {/* PAINEL ⚖️ APURAÇÃO CONSOLIDADA & CAPITAL DE GIRO */}
                      {/* 1. Item, SKU, CFOP e Destinação */}
                      <td className="py-2.5 px-3">
                        <div className="font-bold text-slate-900 leading-snug">{r.name}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="text-[10px] text-slate-400 font-mono">{r.sku}</span>
                          <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold ${
                            isConsumptionItem
                              ? 'bg-slate-100 text-slate-700 border border-slate-300'
                              : r.destination === 'raw_material'
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : r.sector === 'servico' || r.destination === 'service'
                              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {isConsumptionItem 
                              ? 'Uso & Consumo' 
                              : r.destination === 'raw_material'
                              ? 'Matéria-Prima'
                              : r.destination === 'service'
                              ? 'Serviço'
                              : 'Revenda'}
                          </span>
                          {r.cfop && (
                            <span className="text-[9px] font-mono text-slate-600 bg-slate-100 px-1 py-0.2 rounded border border-slate-200">
                              CFOP {r.cfop}
                            </span>
                          )}
                          {r.paymentTermDays !== undefined && r.paymentTermDays > 0 && (
                            <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-1 py-0.2 rounded" title="Prazo de recebimento da venda">
                              PMR: {r.paymentTermDays}d
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 2. Coluna Segregada NCM (Mercadorias/Produtos Físicos) */}
                      <td className="py-2.5 px-2 text-center">
                        {r.ncm && r.ncm !== '-' && r.ncm !== '0000.00.00' && r.fiscalType === 'NCM' ? (
                          <span className="font-mono text-[11px] font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 inline-block">
                            {r.ncm}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-mono text-xs">-</span>
                        )}
                      </td>

                      {/* 3. Coluna Segregada NBS (Serviços/Intangíveis) e Benefício */}
                      <td className="py-2.5 px-2 text-center border-r border-slate-200">
                        {r.fiscalType === 'NBS' ? (
                          <div>
                            <span className="font-mono text-[11px] font-bold text-indigo-800 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200 inline-block">
                              {r.nbs || r.fiscalCode}
                            </span>
                            <span className="text-[9px] text-indigo-600 block mt-0.5 truncate max-w-[130px]">
                              {r.reductionLabel}
                            </span>
                          </div>
                        ) : (
                          <div>
                            <span className="text-slate-300 font-mono text-xs">-</span>
                            <span className="text-[9px] text-slate-500 block mt-0.5 truncate max-w-[130px]" title={r.reductionLabel}>
                              {r.reductionCategory === 'zero'
                                ? 'Alíquota Zero'
                                : r.reductionCategory === 'reducao_60'
                                ? 'Redução 60%'
                                : r.reductionCategory === 'reducao_30'
                                ? 'Redução 30%'
                                : r.reductionCategory === 'seletivo'
                                ? 'Imposto Seletivo'
                                : 'Alíquota Padrão'}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* 4. Custo Compra / NF Entrada (Editável Inline) */}
                      <td className="py-2.5 px-2 text-right">
                        {isSalesOnlyItem ? (
                          <div className="flex flex-col items-end">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500 border border-slate-200 whitespace-nowrap" title="Planilha apenas com notas de venda. Custo de compra não informado.">
                              Venda Direta
                            </span>
                            <div className="inline-flex items-center justify-end gap-1 mt-1">
                              <span className="text-[9px] text-slate-400">R$</span>
                              <input
                                type="number"
                                step="0.10"
                                min="0"
                                value={batchItems[idx]?.costPrice || ''}
                                placeholder="0,00"
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  onUpdateItem(idx, { costPrice: isNaN(val) ? 0 : val });
                                }}
                                className="w-14 px-1 py-0.5 text-right font-medium text-slate-700 bg-white border border-slate-200 focus:border-emerald-500 rounded text-[11px] focus:outline-none"
                                title="Adicionar custo de compra para apurar créditos de entrada"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-end gap-1">
                            <span className="text-[10px] text-slate-400">R$</span>
                            <input
                              type="number"
                              step="0.10"
                              min="0"
                              value={batchItems[idx]?.costPrice ?? r.originalCost}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                onUpdateItem(idx, { costPrice: isNaN(val) ? 0 : val });
                              }}
                              className="w-16 px-1.5 py-0.5 text-right font-bold text-slate-900 bg-slate-50 hover:bg-white border border-slate-200 focus:border-emerald-500 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              title="Clique para editar o custo na NF de entrada"
                            />
                          </div>
                        )}
                      </td>

                      {/* 5. Alíquota Entrada (SEPARADA com CBS e IBS) */}
                      <td className="py-2.5 px-2 text-center bg-emerald-50/20">
                        {isSalesOnlyItem ? (
                          <div className="flex flex-col items-center">
                            <span className="text-[10px] text-slate-400 italic">
                              (Sem NF Compra)
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <span className="px-2 py-0.5 rounded-md font-black text-[11px] bg-emerald-100 text-emerald-900 border border-emerald-200">
                              {r.inputCreditPct > 0 ? `${r.inputCreditPct.toFixed(1)}%` : '0,0%'}
                            </span>
                            {r.inputCreditPct > 0 ? (
                              <span className="text-[9px] text-slate-500 font-mono mt-0.5 whitespace-nowrap">
                                CBS {r.inputCreditCbsRatePct.toFixed(1)}% | IBS {r.inputCreditIbsRatePct.toFixed(1)}%
                              </span>
                            ) : (
                              <span className="text-[9px] text-slate-400 mt-0.5 whitespace-nowrap">
                                (Sem crédito)
                              </span>
                            )}
                            <select
                              value={batchItems[idx]?.supplierTaxRegime || 'regime_geral'}
                              onChange={(e) => onUpdateItem(idx, { supplierTaxRegime: e.target.value as any })}
                              className="mt-1 text-[9px] font-bold bg-white border border-slate-200 rounded px-1 py-0.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                              title="Regime do fornecedor na NF de entrada"
                            >
                              <option value="regime_geral">Geral (Crédito Integral)</option>
                              <option value="simples_nacional">Simples (4%)</option>
                              <option value="isento_zero">Cesta Zero (0%)</option>
                            </select>
                          </div>
                        )}
                      </td>

                      {/* 6. Crédito Entrada (Valor R$ com CBS e IBS discriminados) */}
                      <td className="py-2.5 px-2 text-right border-r border-slate-200">
                        <div className="font-bold text-emerald-700 text-xs">
                          +{formatBRL(r.inputCreditVal)}
                        </div>
                        {r.inputCreditVal > 0 && (
                          <span className="text-[9px] text-slate-500 block font-mono whitespace-nowrap">
                            CBS: +{formatBRL(r.inputCreditCbsVal)} | IBS: +{formatBRL(r.inputCreditIbsVal)}
                          </span>
                        )}
                      </td>

                      {/* 7. Preço de Venda Atual */}
                      <td className="py-2.5 px-2 text-right text-slate-600">
                        {isConsumptionItem ? (
                          <span className="text-slate-400 italic text-[11px]">(Consumo)</span>
                        ) : (
                          formatBRL(r.currentSalePrice)
                        )}
                      </td>

                      {/* 8. Alíquota Saída (SEPARADA com CBS e IBS da Nova Reforma) */}
                      <td className="py-2.5 px-2 text-center bg-indigo-50/20">
                        {isConsumptionItem ? (
                          <div className="flex flex-col items-center">
                            <span className="px-2 py-0.5 rounded-md font-bold text-[10px] bg-slate-100 text-slate-600 border border-slate-200">
                              Não Aplicável
                            </span>
                            <span className="text-[9px] text-slate-400 mt-0.5">
                              (Sem débito saída)
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <span className={`px-2 py-0.5 rounded-md font-black text-[11px] border ${
                              r.reductionCategory === 'zero'
                                ? 'bg-emerald-100 text-emerald-900 border-emerald-200'
                                : r.reductionCategory === 'reducao_60'
                                ? 'bg-blue-100 text-blue-900 border-blue-200'
                                : r.reductionCategory === 'reducao_30'
                                ? 'bg-indigo-100 text-indigo-900 border-indigo-200'
                                : r.reductionCategory === 'seletivo'
                                ? 'bg-amber-100 text-amber-900 border-amber-200'
                                : 'bg-slate-100 text-slate-800 border-slate-200'
                            }`}>
                              {r.totalTaxRatePct.toFixed(1)}%
                            </span>
                            <span className="text-[9px] text-slate-500 font-mono mt-0.5 whitespace-nowrap">
                              CBS {r.cbsRatePct.toFixed(1)}% | IBS {r.ibsRatePct.toFixed(1)}%
                            </span>
                            <select
                              value={r.reductionCategory}
                              onChange={(e) => onUpdateItem(idx, { taxReduction: e.target.value as any })}
                              className="mt-1 text-[9px] font-bold bg-white border border-slate-200 rounded px-1 py-0.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
                            >
                              <option value="zero">Zero (Redução Integral)</option>
                              <option value="reducao_60">Red. 60%</option>
                              <option value="reducao_30">Red. 30%</option>
                              <option value="padrao">Padrão</option>
                              <option value="seletivo">Seletivo</option>
                            </select>
                          </div>
                        )}
                      </td>

                      {/* 9. Novo Preço Sugerido (Proteção CBS/IBS) */}
                      <td className="py-2.5 px-3 text-right bg-emerald-50/50">
                        {isConsumptionItem ? (
                          <div>
                            <span className="text-xs font-bold text-slate-600 block">
                              Crédito Operacional
                            </span>
                            <span className="text-[9px] text-emerald-700 font-semibold block">
                              Economia no Caixa
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className="font-black text-emerald-900 text-sm">
                              {formatBRL(r.suggestedSalePrice)}
                            </div>
                            {isSalesOnlyItem ? (
                              <span className="text-[9px] text-emerald-700 font-medium block" title="Preço de venda atual preservado na íntegra">
                                (Preço Atual Preservado)
                              </span>
                            ) : r.simulatedSalePrice !== r.suggestedSalePrice ? (
                              <span className="text-[10px] text-indigo-700 font-bold block" title="Preço editado ativo">
                                Simulado: {formatBRL(r.simulatedSalePrice)}
                              </span>
                            ) : null}
                          </>
                        )}
                      </td>

                      {/* 10. Débito Saída (Valor R$ com CBS e IBS discriminados) */}
                      <td className="py-2.5 px-2 text-right border-r border-slate-200">
                        {isConsumptionItem ? (
                          <span className="text-xs text-slate-400">R$ 0,00</span>
                        ) : (
                          <>
                            <div className="font-bold text-rose-700 text-xs">
                              {formatBRL(r.taxOnSaleVal)}
                            </div>
                            {r.taxOnSaleVal > 0 && (
                              <span className="text-[9px] text-slate-500 block font-mono whitespace-nowrap">
                                CBS: {formatBRL(r.taxOnSaleCbsVal)} | IBS: {formatBRL(r.taxOnSaleIbsVal)}
                              </span>
                            )}
                          </>
                        )}
                      </td>

                      {/* 11. Imposto a Recolher no Split (Saída - Entrada com CBS e IBS discriminados) */}
                      <td className="py-2.5 px-2 text-right">
                        {isConsumptionItem ? (
                          <div>
                            <div className="font-bold text-emerald-700 text-xs">
                              -{formatBRL(r.inputCreditVal)}
                            </div>
                            <span className="text-[9px] text-slate-400 block whitespace-nowrap">
                              (Crédito a Compensar)
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className="font-black text-slate-800 text-xs">
                              {formatBRL(r.netTaxPayableVal)}
                            </div>
                            {r.netTaxPayableVal > 0 ? (
                              <span className="text-[9px] text-slate-500 block font-mono whitespace-nowrap">
                                CBS: {formatBRL(r.cbsToPayVal)} | IBS: {formatBRL(r.ibsToPayVal)}
                              </span>
                            ) : (
                              <span className="text-[9px] text-emerald-700 block font-medium">
                                (Zero a recolher)
                              </span>
                            )}
                          </>
                        )}
                      </td>

                      {/* 12. Margem Projetada */}
                      <td className="py-2.5 px-2 text-right font-bold text-slate-800">
                        {isConsumptionItem ? (
                          <div>
                            <span className="text-xs font-bold text-emerald-700 block">
                              +{formatBRL(r.inputCreditVal)}
                            </span>
                            <span className="text-[9px] text-slate-400 font-normal">
                              Ganho da Reforma
                            </span>
                          </div>
                        ) : (
                          <>
                            {formatPct(r.projectedMarginPct)}
                            <span className="block text-[9px] text-slate-400 font-normal">
                              {formatBRL(r.projectedMarginVal)}
                            </span>
                          </>
                        )}
                      </td>

                      {/* 13. Diagnóstico */}
                      <td className="py-2.5 px-3 text-center border-r border-slate-200">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${
                          r.status === 'HEALTHY'
                            ? 'bg-emerald-100 text-emerald-800'
                            : r.status === 'COMPRESSED'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}>
                          {r.statusLabel}
                        </span>
                      </td>

                      {/* 14. Botão Simular NF */}
                      <td className="py-2.5 px-2 text-center">
                        <button
                          type="button"
                          onClick={() => onOpenSimulationModal(idx)}
                          className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 transition-colors inline-flex items-center justify-center gap-1 text-[11px] font-bold cursor-pointer"
                          title="Abrir Simulador de Nota Fiscal de Entrada e Saída"
                        >
                          <Zap className="w-3.5 h-3.5 text-emerald-700" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Metodologia de Precificação e Proteção de Margem (Sem Jargões Internos) */}
      <div className="p-4 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed flex items-start gap-2.5">
        <HelpCircle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <span>
          <strong>Metodologia de Precificação & Proteção de Caixa:</strong> O <strong>Novo Preço Sugerido</strong> recalcula o IVA Dual (CBS e IBS) incidindo por fora sobre o custo líquido, garantindo que os novos tributos não corroam sua margem. O fechamento comercial aplica arredondamento protetivo em degraus de R$ 5,00 para cima, eliminando centavos residuais e resguardando a rentabilidade nas vendas. Você pode editar qualquer custo de entrada, alíquota ou preço de saída na tabela para simular seus cenários fiscais em tempo real.
        </span>
      </div>
    </div>
  );
}
